/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-10-06 11:39:10
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { Icon, Link, TextStyle } from '@shopify/polaris';
import { DragHandleMinor, ExternalSmallMinor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import { Field } from 'redux-form';

import { RenderToggleSwitch } from 'src/components/views/common/forms/render-toggle-switch';
import { RenderAutocomplete } from 'src/components/views/common/autocomplete-hoc';
import RenderAutocompleteV2 from 'src/components/views/common/autocomplete-hoc-v2';
import { RenderSelect } from 'src/components/views/common/forms/render-select';

import { routes } from 'src/routes';
import { getShop } from 'src/helpers';

import {
  RECOMMENDATION_WIDGET_RULE_BASED_RECOMMENDATION_TYPES_LABEL as RULE_BASE_TYPES_LABEL,
  RECOMMENDATION_WIDGET_RULE_BASED_RECOMMENDATION_TYPES as RULE_BASED_TYPES
} from 'src/constants/recommendation';

import type { RuleType } from 'src/models/recommendation';
import type { RecommendationWidgetType } from 'src/models/recommendation';

type Props = {
  index: number,
  rule: RuleType,
  dragHandleProps: DraggableProvidedDragHandleProps,
  formValues: RecommendationWidgetType,
  collections: Array<{ value: string, label: string }>,
  filterData: Object,
  updateField: Function,
  isLimitRuleBased: boolean,
  host: string
};

type ComposedProps = WithI18nProps & Props;

class RuleBasedItem extends Component<ComposedProps> {
  render() {
    const { i18n, dragHandleProps, index, rule, formValues, isLimitRuleBased } = this.props;

    const { type = '' } = rule;
    const label = i18n.translate(RULE_BASE_TYPES_LABEL[type]);
    const enableRule = _.get(formValues, `ruleBasedList[${index}].status`, false);
    const shop = getShop();

    return (
      <div className="rule-based-item">
        <div className="rule-based-item_draggable" {...dragHandleProps}>
          <Icon source={DragHandleMinor} color="subdued" />
        </div>

        <div className="rule-based-item_details">
          <Field
            name={`ruleBasedList[${index}].status`}
            component={RenderToggleSwitch}
            label={label}
            disabled={!enableRule && isLimitRuleBased}
            onChange={(e, value) => this.changeStatus(value, index, type)}
          />
          {this.renderRuleBasedDetails(type, shop)}
        </div>
      </div>
    );
  }

  renderRuleBasedDetails = (type: string, shop: string) => {
    const { i18n, index, formValues, collections, metafields, host } = this.props;
    const enableRule = _.get(formValues, `ruleBasedList[${index}].status`, false);
    if (enableRule) {
      // Show detail fields
      switch (type) {
        case RULE_BASED_TYPES.SAME_COLLECTION: {
          const excluded = _.get(formValues, `ruleBasedList[${index}].excluded`, []);
          return (
            <div className="rule-based-item_groupFields">
              <Field
                name={`ruleBasedList[${index}].excluded`}
                component={RenderAutocomplete}
                label={i18n.translate(
                  'AdminHTML.Recommendation.WidgetItem.ruleBased_IgnoreCollections_Label'
                )}
                value={excluded}
                options={collections}
                placeholder={i18n.translate(
                  'AdminHTML.Recommendation.WidgetItem.ruleBased_IgnoreCollections_Placeholder'
                )}
                tooltip={i18n.translate(
                  'AdminHTML.Recommendation.WidgetItem.ruleBased_IgnoreCollections_Tooltip'
                )}
                extraSearchFields={['value', 'handle']}
              />
            </div>
          );
        }
        case RULE_BASED_TYPES.SAME_TAGS: {
          const excluded = _.get(formValues, `ruleBasedList[${index}].excluded`, []);
          return (
            <div className="rule-based-item_groupFields">
              <Field
                name={`ruleBasedList[${index}].excluded`}
                component={RenderAutocompleteV2}
                label={i18n.translate(
                  'AdminHTML.Recommendation.WidgetItem.ruleBased_IgnoreTags_Label'
                )}
                value={excluded}
                type={'tags'}
                placeholder={i18n.translate(
                  'AdminHTML.Recommendation.WidgetItem.ruleBased_IgnoreTags_Placeholder'
                )}
                tooltip={i18n.translate(
                  'AdminHTML.Recommendation.WidgetItem.ruleBased_IgnoreTags_Tooltip'
                )}
              />
            </div>
          );
        }
        case RULE_BASED_TYPES.SAME_METAFIELD_1:
        case RULE_BASED_TYPES.SAME_METAFIELD_2:
        case RULE_BASED_TYPES.SAME_METAFIELD_3: {
          const { filterData } = this.props;
          const metafield = _.get(formValues, `ruleBasedList[${index}].metafield`, '');
          const metafieldValues = _.get(filterData, `${metafield}`, []);
          const excluded = _.get(formValues, `ruleBasedList[${index}].excluded`, []);

          // Remove existed metafields
          const ruleBasedList = _.get(formValues, 'ruleBasedList', []);
          let selectedMetafields = [];
          _.map(ruleBasedList, (rule, ruleIndex) => {
            if (ruleIndex !== index && !_.isEmpty(rule.metafield)) {
              selectedMetafields.push(rule.metafield);
            }
          });
          const metafieldOptions = _.filter(metafields, metafield => {
            return !_.includes(selectedMetafields, metafield.value);
          });

          return (
            <div className="rule-based-item_groupFields">
              <div className="widget-based-same-metafield">
                <TextStyle variation="subdued">
                  {i18n.translate(
                    'AdminHTML.Recommendation.WidgetItem.ruleBased_AddMetafield_Helper',
                    {
                      t: (
                        <Link external url={`${routes.metafield.view()}?shop=${shop}&host=${host}`}>
                          {i18n.translate(
                            'AdminHTML.Recommendation.WidgetItem.ruleBased_AddMetafield_Helper_t'
                          )}
                          <Icon source={ExternalSmallMinor} color={'interactive'}></Icon>
                        </Link>
                      )
                    }
                  )}
                </TextStyle>
              </div>
              <div className="rule-based-item_field">
                <Field
                  name={`ruleBasedList[${index}].metafield`}
                  component={RenderSelect}
                  label={i18n.translate(
                    'AdminHTML.Recommendation.WidgetItem.ruleBased_Metafield_Label'
                  )}
                  tooltip={i18n.translate(
                    'AdminHTML.Recommendation.WidgetItem.ruleBased_Metafield_Tooltip'
                  )}
                  options={metafieldOptions}
                  placeholder={i18n.translate(
                    'AdminHTML.Recommendation.WidgetItem.ruleBased_Metafield_Placeholder'
                  )}
                  onChange={() => this.changeMetafield(index)}
                />
              </div>

              <div className="rule-based-item_subField">
                <Field
                  name={`ruleBasedList[${index}].excluded`}
                  component={RenderAutocomplete}
                  label={i18n.translate(
                    'AdminHTML.Recommendation.WidgetItem.ruleBased_IgnoreMetafield_Label'
                  )}
                  value={excluded}
                  options={metafieldValues}
                  placeholder={i18n.translate(
                    'AdminHTML.Recommendation.WidgetItem.ruleBased_IgnoreMetafield_Placeholder'
                  )}
                  tooltip={i18n.translate(
                    'AdminHTML.Recommendation.WidgetItem.ruleBased_IgnoreMetafield_Tooltip'
                  )}
                  disabled={_.isEmpty(metafield)}
                />
              </div>
            </div>
          );
        }
        default:
      }
      return '';
    }
  };

  changeStatus = (value, index, type) => {
    // Remove all value inside
    const { updateField } = this.props;
    if (!value) {
      switch (type) {
        case RULE_BASED_TYPES.SAME_COLLECTION:
        case RULE_BASED_TYPES.SAME_TAGS: {
          updateField(`ruleBasedList[${index}].excluded`, []);
          break;
        }
        case RULE_BASED_TYPES.SAME_METAFIELD_1:
        case RULE_BASED_TYPES.SAME_METAFIELD_2:
        case RULE_BASED_TYPES.SAME_METAFIELD_3: {
          updateField(`ruleBasedList[${index}].metafield`, '');
          updateField(`ruleBasedList[${index}].excluded`, []);
          break;
        }
        default:
      }
    }
  };

  changeMetafield = index => {
    // Remove excluded values
    const { updateField } = this.props;
    updateField(`ruleBasedList[${index}].excluded`, []);
  };
}

export default withI18n()(RuleBasedItem);
